export interface ChatCitation {
  title: string;
  url: string;
}

export type ChatProposalStatus = 'pending' | 'working' | 'confirmed' | 'declined' | 'failed';

/**
 * A mutating action the assistant wants to take, surfaced for confirmation.
 * Only appears on surfaces whose transport implements `respondToAction`
 * (the authenticated dashboard agent) — the anonymous docs bot never emits
 * these.
 */
export interface ChatProposal {
  actionId: string;
  summary: string;
  /** Flat, human-readable inputs shown on the confirmation card. */
  details?: Record<string, string | number | boolean>;
  status: ChatProposalStatus;
}

/** A file in the composer tray, as the widget displays it. */
export interface ChatAttachmentItem {
  /** Stable id for list rendering and removal. */
  id: string;
  name: string;
  /** Pre-formatted size, e.g. "240 KB". */
  sizeLabel?: string;
  status: 'uploading' | 'ready' | 'error';
  /** Why the file can't be sent; shown on the chip. */
  error?: string;
}

/** A ready attachment handed to the transport with the message it belongs to. */
export interface ChatSentAttachment {
  id: string;
  name: string;
  /**
   * Opaque handle from the host (e.g. a stored-file id). The widget never reads
   * it; the host's transport does.
   */
  ref: string;
}

/**
 * File attachments for a host that supports them (the authenticated dashboard
 * agent). The host owns uploading; the widget only renders the tray, the
 * attach button and the drop zone, and hands `ready` items to `transport.send`.
 * Omit it and the widget shows no attachment UI at all (the public docs bot).
 */
export interface ChatAttachmentsController {
  items: ChatAttachmentItem[];
  /** Attachments ready to send, with the host's handle for each. */
  ready: ChatSentAttachment[];
  addFiles(files: File[]): void;
  remove(id: string): void;
  /** Empty the tray; called once a message carrying the attachments is sent. */
  clear(): void;
  /** True while an upload is in flight or a failed item is still in the tray. */
  blocked: boolean;
  /** True when no more files can be added to this message. */
  full: boolean;
  /** File-picker `accept` attribute. */
  accept?: string;
}

export interface ChatWidgetMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Names of the files sent with this (user) message. */
  attachments?: Array<{ name: string }>;
  citations?: ChatCitation[];
  isError?: boolean;
  proposals?: ChatProposal[];
}

export interface ChatTransportReply {
  reply: string;
  citations?: ChatCitation[];
  proposals?: ChatProposal[];
}

/**
 * Pluggable backend for the widget — the component is surface-agnostic.
 *
 * - Website (anonymous): POSTs the conversation to the public docs
 *   support-chat endpoint. Answers only — no `respondToAction`.
 * - Dashboard (authenticated): talks to the agent SSE endpoint, may return
 *   `proposals` for mutating actions, and implements `respondToAction` so the
 *   widget can confirm/decline them inline.
 */
export interface ChatTransport {
  send(
    messages: Array<Pick<ChatWidgetMessage, 'role' | 'content'>>,
    options?: {
      onDelta?: (text: string) => void;
      /** Files attached to the latest message (only when the host supplies attachments). */
      attachments?: ChatSentAttachment[];
    },
  ): Promise<ChatTransportReply>;
  /**
   * Confirm or decline a proposed action, then return the continuation of the
   * turn (further text and/or proposals). Present only when the surface
   * supports executing actions; absent on the anonymous docs bot.
   */
  respondToAction?(
    actionId: string,
    decision: 'confirm' | 'reject',
  ): Promise<ChatTransportReply>;
}

/** Thrown by transports to surface a user-appropriate message in the thread. */
export class ChatTransportError extends Error {
  constructor(
    message: string,
    readonly retryable = false,
  ) {
    super(message);
    this.name = 'ChatTransportError';
  }
}
